import React from "react";
import Modal from "../../components/modal";
import { groupPeriodsByUser, resolveMonthlyCost } from "../../utils/user_contract";

const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n));

const ROW_DEFINITIONS = {
  Invoicing: {
    source: "Invoice",
    field: "netTotal",
    description: "Revenue from client invoices (sent=yes, not written off) + Stripe bank income for Walego.",
  },
  Grants: {
    source: "Bank",
    field: "amount",
    description: "Subventions and grants. Bank transactions with sub_category=SUBVENTION.",
  },
  "Employee Costs": {
    source: "Employee",
    field: "total_monthly_cost",
    description: "Monthly cost (total_monthly_cost) of all non-freelance employees active during the month. Based on employee records, not activity logs.",
  },
  "Freelance Monthly Costs": {
    source: "FreelanceMonthly",
    field: "value",
    description: "Actual months: real payments (type=activity, any status except CANCELED/ERROR) of monthly freelancers. Forecast months: projected total_monthly_cost of still-active freelancers.",
  },
  "Freelance Daily Costs": {
    source: "Freelance",
    field: "cost",
    description: "Cost of daily freelancers on billable projects, grouped by person. Excludes timeoff and monthly freelancers.",
  },
  "Other Costs": {
    source: "Bank",
    field: "amount",
    description: "General operating costs. Bank transactions with category=GENERAL_COST.",
  },
  "Business Introducer Costs": {
    source: "Bank",
    field: "amount",
    description: "Commissions paid to business introducers. Bank transactions with sub_category=BUSINESS_INTRODUCER_COST.",
  },
  Loan: {
    source: "Bank",
    field: "amount",
    description: "Loan repayments. Bank transactions with category=BANK_LOAN.",
  },
  Taxes: {
    source: "Bank",
    field: "amount",
    description: "Taxes. Bank transactions with category=TAXES.",
  },
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function PnlDetailModal({ isOpen, onClose, rowLabel, monthIndex, year, rawData }) {
  if (!isOpen || !rowLabel || monthIndex === null) return null;

  const def = ROW_DEFINITIONS[rowLabel];
  if (!def) return null;

  const records = getRecordsForCell(rowLabel, monthIndex, year, rawData);
  const total = records.reduce((sum, r) => sum + (r.value || 0), 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full md:w-[800px]">
      <div className="p-6">
        {/* Header */}
        <h2 className="text-lg font-bold text-gray-900 mb-1">
          {rowLabel} — {MONTHS[monthIndex]} {year}
        </h2>

        {/* Definition */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-xs text-blue-800">
            <span className="font-semibold">Source:</span> <code className="bg-blue-100 px-1 rounded">{def.source}</code> ·{" "}
            <span className="font-semibold">Field:</span> <code className="bg-blue-100 px-1 rounded">{def.field}</code>
          </p>
          <p className="text-xs text-blue-700 mt-1">{def.description}</p>
        </div>

        {/* Detail table */}
        {records.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No records for this month</p>
        ) : (
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  {getColumns(def.source).map((col) => (
                    <th key={col.key} className={`py-2 px-2 font-medium ${col.align === "right" ? "text-right" : ""}`}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => {
                  const url = r._id ? (r.isInvoice ? `/invoice/${r._id}` : `/bank?bank_id=${r._id}`) : null;
                  const receivedTint = r.received === "yes" ? "bg-green-50" : r.received === "no" ? "bg-red-50" : "";
                  return (
                    <tr key={i} onClick={() => url && window.open(url, "_blank")} className={`border-b border-gray-100 hover:bg-gray-50 ${receivedTint} ${url ? "cursor-pointer" : ""}`}>
                      {getColumns(def.source).map((col) => (
                        <td key={col.key} className={`py-1.5 px-2 ${col.align === "right" ? "text-right tabular-nums" : "text-gray-700"}`}>
                          {col.format ? col.format(r[col.key]) : r[col.key] || "—"}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 font-bold">
                  <td className="py-2 px-2" colSpan={getColumns(def.source).length - 1}>
                    Total ({records.length} records)
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmt(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

function getColumns(source) {
  if (source === "Invoice") {
    return [
      { key: "projectName", label: "Project" },
      { key: "name", label: "Invoice" },
      { key: "received", label: "Received", format: (v) => (v === undefined ? "—" : v === "yes" ? "Yes" : "No") },
      { key: "value", label: "Net Total", align: "right", format: fmt },
    ];
  }
  if (source === "Activity") {
    return [
      { key: "userName", label: "Person" },
      { key: "projectName", label: "Project" },
      { key: "total", label: "Days", align: "right", format: (v) => (v ? (v / 8).toFixed(1) : "—") },
      { key: "userContract", label: "Contract" },
      { key: "date", label: "Date", format: (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—") },
      { key: "value", label: "Cost", align: "right", format: fmt },
    ];
  }
  if (source === "Employee") {
    return [
      { key: "name", label: "Employee" },
      { key: "contract", label: "Contract" },
      { key: "position", label: "Position" },
      { key: "value", label: "Monthly Cost", align: "right", format: fmt },
    ];
  }
  if (source === "FreelanceMonthly") {
    return [
      { key: "name", label: "Person" },
      { key: "position", label: "Position" },
      { key: "status", label: "Status" },
      { key: "value", label: "Monthly Cost", align: "right", format: fmt },
    ];
  }
  if (source === "Freelance") {
    return [
      { key: "name", label: "Person" },
      { key: "days", label: "Days", align: "right", format: (v) => (v ? v.toFixed(1) : "—") },
      { key: "paymentType", label: "Type" },
      { key: "value", label: "Cost", align: "right", format: fmt },
    ];
  }
  // Bank
  return [
    { key: "name", label: "Description" },
    { key: "category", label: "Category" },
    { key: "sub_category", label: "Sub-category" },
    { key: "date", label: "Date", format: (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—") },
    { key: "value", label: "Amount", align: "right", format: fmt },
  ];
}

function getRecordsForCell(rowLabel, monthIndex, year, rawData) {
  const { invoices = [], activities = [], banks = [], employees = [], freelanceMonthly = [], payments = [], contracts = [] } = rawData;
  const periodsByUser = groupPeriodsByUser(contracts);

  const inMonth = (date) => {
    const d = new Date(date);
    return d.getMonth() === monthIndex && d.getFullYear() === year;
  };

  switch (rowLabel) {
    case "Invoicing": {
      const invoiceRecords = invoices.filter((inv) => !inv.written_off && inMonth(inv.date)).map((inv) => ({
        _id: inv._id,
        isInvoice: true,
        name: inv.name,
        projectName: inv.projectName,
        date: inv.date,
        value: inv.netTotal || 0,
        received: inv.received,
      }));
      const bankIncomeRecords = banks
        .filter((b) => b.category === "INCOME" && b.business_unit_name === "Walego" && inMonth(b.date))
        .map((b) => ({
          _id: b._id,
          name: b.name,
          projectName: "Walego (Stripe)",
          date: b.date,
          value: b.amount || 0,
        }));
      return [...invoiceRecords, ...bankIncomeRecords];
    }

    case "Grants":
      return banks.filter((b) => b.sub_category === "SUBVENTION" && inMonth(b.date)).map((b) => ({
        _id: b._id,
        name: b.name,
        category: b.category,
        sub_category: b.sub_category,
        date: b.date,
        value: b.amount || 0,
      }));

    case "Employee Costs": {
      const monthStart = new Date(year, monthIndex, 1);
      const monthEnd = new Date(year, monthIndex + 1, 0);
      return employees
        .filter((emp) => {
          const start = emp.starting_date ? new Date(emp.starting_date) : null;
          const exit = emp.exit_date ? new Date(emp.exit_date) : null;
          if (start && start > monthEnd) return false;
          if (exit && exit < monthStart) return false;
          return resolveMonthlyCost(periodsByUser, emp, monthEnd) > 0;
        })
        .map((emp) => ({ name: emp.name, contract: emp.contract, position: emp.position || "—", value: resolveMonthlyCost(periodsByUser, emp, monthEnd) }));
    }

    case "Freelance Monthly Costs": {
      const isForecastMonth = year === new Date().getFullYear() ? monthIndex > new Date().getMonth() : year > new Date().getFullYear();
      if (isForecastMonth) {
        const monthStart = new Date(year, monthIndex, 1);
        const monthEnd = new Date(year, monthIndex + 1, 0);
        return freelanceMonthly
          .filter((freelancer) => {
            if (freelancer.status !== "active") return false;
            const start = freelancer.starting_date ? new Date(freelancer.starting_date) : null;
            const exit = freelancer.exit_date ? new Date(freelancer.exit_date) : null;
            if (start && start > monthEnd) return false;
            if (exit && exit < monthStart) return false;
            return resolveMonthlyCost(periodsByUser, freelancer, monthEnd) > 0;
          })
          .map((freelancer) => ({ name: freelancer.name, position: freelancer.position || "—", status: "Forecast", value: resolveMonthlyCost(periodsByUser, freelancer, monthEnd) }));
      }
      const freelanceMonthlyIds = new Set(freelanceMonthly.map((freelancer) => freelancer._id));
      const positionById = new Map(freelanceMonthly.map((freelancer) => [freelancer._id, freelancer.position]));
      return payments
        .filter((payment) => freelanceMonthlyIds.has(payment.user_id) && inMonth(payment.date))
        .map((payment) => ({ name: payment.user_name, position: positionById.get(payment.user_id) || "—", status: payment.status, value: payment.value || 0 }));
    }

    case "Freelance Daily Costs": {
      const freelanceMonthlyIds = new Set(freelanceMonthly.map((freelancer) => freelancer._id));
      const freelanceActivities = activities.filter((activity) => activity.userContract === "FREELANCE" && activity.userPaymentType !== "MONTHLY" && !freelanceMonthlyIds.has(activity.userId) && activity.projectType !== "timeoff" && inMonth(activity.date));
      const byPerson = {};
      for (const activity of freelanceActivities) {
        const key = activity.userName || activity.userId;
        if (!byPerson[key]) byPerson[key] = { name: key, days: 0, value: 0, paymentType: activity.userPaymentType || "—" };
        byPerson[key].days += (activity.total || 0) / 8;
        byPerson[key].value += activity.cost || 0;
      }
      return Object.values(byPerson).sort((a, b) => b.value - a.value);
    }

    case "Other Costs":
      return banks.filter((b) => b.category === "GENERAL_COST" && inMonth(b.date)).map((b) => ({
        _id: b._id,
        name: b.name,
        category: b.category,
        sub_category: b.sub_category,
        date: b.date,
        value: b.amount || 0,
      }));

    case "Business Introducer Costs":
      return banks.filter((b) => b.sub_category === "BUSINESS_INTRODUCER_COST" && inMonth(b.date)).map((b) => ({
        _id: b._id,
        name: b.name,
        category: b.category,
        sub_category: b.sub_category,
        date: b.date,
        value: b.amount || 0,
      }));

    case "Loan":
      return banks.filter((b) => b.category === "BANK_LOAN" && inMonth(b.date)).map((b) => ({
        _id: b._id,
        name: b.name,
        category: b.category,
        sub_category: b.sub_category,
        date: b.date,
        value: b.amount || 0,
      }));

    case "Taxes":
      return banks.filter((b) => b.category === "TAXES" && inMonth(b.date)).map((b) => ({
        _id: b._id,
        name: b.name,
        category: b.category,
        sub_category: b.sub_category,
        date: b.date,
        value: b.amount || 0,
      }));

    default:
      return [];
  }
}
